import React, { useState, useEffect } from 'react';
import { getDashboardStats, getConnections, getTokenUsageStats } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Database, 
  MessageSquare, 
  Users, 
  Clock,
  ArrowRight,
  Sparkles,
  BookOpen,
  Zap,
  ExternalLink,
  TrendingUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [connections, setConnections] = useState([]);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [getDashboardStats(), getConnections()];
        
        // Fetch token usage for admins
        if (user?.role === 'admin') {
          promises.push(getTokenUsageStats().catch(err => {
            console.log('Token usage not available:', err);
            return null;
          }));
        }
        
        const results = await Promise.all(promises);
        setStats(results[0]);
        setConnections(results[1]);
        if (results[2]) setTokenUsage(results[2]);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.role]);

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statCards = [
    {
      title: 'Database Connections',
      value: stats?.connections || 0,
      icon: Database,
      color: 'blue',
      description: 'Active MySQL connections'
    },
    {
      title: 'AI Queries',
      value: stats?.queries || 0,
      icon: MessageSquare,
      color: 'indigo',
      description: 'Total queries made'
    },
    {
      title: 'Team Members',
      value: stats?.users || 0,
      icon: Users,
      color: 'emerald',
      description: 'Platform users'
    },
    {
      title: 'Knowledge Entries',
      value: stats?.knowledge_entries || 0,
      icon: BookOpen,
      color: 'amber',
      description: 'AI learning entries'
    }
  ];

  const suggestedQueries = [
    "Are my deposit numbers low compared to last week?",
    "Show me the top 10 users by total wager amount",
    "What's the win/loss ratio across all brands?",
    "Which game provider has the highest turnover?",
    "Show daily deposit trends for the last month"
  ];

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-gray-500 mt-1">
          Here's what's happening with your analytics platform
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card 
            key={stat.title}
            className="bg-white border-gray-200 hover:shadow-lg transition-all duration-300 group"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {stat.value}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{stat.description}</p>
                </div>
                <div className="p-3 rounded-xl bg-[#D4AF37]/10 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <stat.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* LLM Credit Balance Info - Admin Only */}
      {user?.role === 'admin' && (
        <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200/50" data-testid="llm-credit-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-indigo-100">
                  <Zap className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">AI Credits (Claude Sonnet 4.5)</h3>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Powered by Emergent Universal Key • Check your balance and top up if needed
                  </p>
                </div>
              </div>
              <a
                href="https://emergent.sh/profile"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
                data-testid="check-credits-btn"
              >
                <span>Check Balance</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            
            {/* Token Usage Stats */}
            {tokenUsage && (
              <div className="mt-4 pt-4 border-t border-indigo-200/50">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-indigo-500" />
                  <span className="text-sm font-medium text-gray-700">Estimated Token Usage</span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white/60 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Today</p>
                    <p className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {formatNumber(tokenUsage.today?.total_tokens || 0)}
                    </p>
                    <p className="text-xs text-gray-400">{tokenUsage.today?.total_queries || 0} queries</p>
                  </div>
                  <div className="bg-white/60 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Last 7 Days</p>
                    <p className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {formatNumber(tokenUsage.last_7_days?.total_tokens || 0)}
                    </p>
                    <p className="text-xs text-gray-400">{tokenUsage.last_7_days?.total_queries || 0} queries</p>
                  </div>
                  <div className="bg-white/60 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Last 30 Days</p>
                    <p className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {formatNumber(tokenUsage.last_30_days?.total_tokens || 0)}
                    </p>
                    <p className="text-xs text-gray-400">{tokenUsage.last_30_days?.total_queries || 0} queries</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2 italic">
                  * Estimates based on ~4 chars/token. Check Emergent profile for actual usage.
                </p>
              </div>
            )}
            
            <div className="mt-3 pt-3 border-t border-indigo-200/50">
              <p className="text-xs text-gray-500">
                <strong>Tip:</strong> Go to Profile → Universal Key on Emergent to view remaining credits and enable auto top-up.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Start Card */}
        <Card className="bg-gradient-to-br from-[#D4AF37] to-[#B8962E] border-0 text-white">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-white/80" />
              <CardTitle className="text-white">Start Analyzing</CardTitle>
            </div>
            <CardDescription className="text-white/70">
              Ask questions about your database in natural language
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {suggestedQueries.slice(0, 3).map((query, index) => (
                <button
                  key={index}
                  onClick={() => navigate('/query', { state: { query } })}
                  className="w-full text-left p-3 rounded-lg bg-white/10 hover:bg-white/20 border border-white/10 hover:border-white/20 transition-all group"
                  data-testid={`suggested-query-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/90 group-hover:text-white transition-colors">
                      "{query}"
                    </span>
                    <ArrowRight className="w-4 h-4 text-white/50 group-hover:text-white transition-colors" />
                  </div>
                </button>
              ))}
            </div>
            <Button
              onClick={() => navigate('/query')}
              className="w-full bg-white text-[#D4AF37] hover:bg-gray-100"
              data-testid="go-to-query-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Open AI Query Interface
            </Button>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="bg-white border-gray-200">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-400" />
              <CardTitle className="text-gray-900">Recent Queries</CardTitle>
            </div>
            <CardDescription className="text-gray-500">
              Your latest AI-powered analyses
            </CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.recent_queries?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_queries.map((query, index) => (
                  <div
                    key={query.id || index}
                    className="p-3 rounded-lg bg-gray-50 border border-gray-100 hover:border-gray-200 transition-colors cursor-pointer"
                    onClick={() => navigate('/history')}
                  >
                    <p className="text-sm text-gray-700 line-clamp-1">
                      {query.query}
                    </p>
                    <p className="text-xs text-gray-400 mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {formatDate(query.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No queries yet</p>
                <p className="text-gray-400 text-xs">Start by asking a question about your data</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Connections Overview */}
      <Card className="bg-white border-gray-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="w-5 h-5 text-gray-400" />
              <CardTitle className="text-gray-900">Database Connections</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/connections')}
              className="text-[#D4AF37] hover:text-[#D4AF37]/80"
              data-testid="manage-connections-btn"
            >
              Manage
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {connections.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {connections.map((conn) => (
                <div
                  key={conn.id}
                  className="p-4 rounded-lg bg-gray-50 border border-gray-100 hover:border-[#D4AF37]/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full status-online" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{conn.name}</p>
                      <p className="text-xs text-gray-500" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {conn.host}:{conn.port}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      Database: <span className="text-gray-700">{conn.database}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Database className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No connections configured</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/connections')}
                className="mt-3 border-gray-200 text-gray-700 hover:bg-gray-50"
                data-testid="add-first-connection-btn"
              >
                Add your first connection
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
